import { faBolt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Badge,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import socialApiClient from "../../../cores/helpers/services/social-api-client";
import { Plan } from "../../../cores/me/interfaces/api/get-meCompany";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import { useRouterCore } from "../../../cores/router";
import {
  WEMBII_AI_CREDITS_REFRESH_EVENT,
  WEMBII_AI_CREDITS_TOGGLE_ATTR,
} from "../../../constants/dom.constant";
import { BuyCreditsModal } from "../credits/BuyCreditsModal";
import { LowCreditsPromptOverlay } from "../../layout/LowCreditsPromptOverlay";

interface Props {
  companyPlan?: Plan | null;
  className?: string;
}

const LOW_CREDITS_THRESHOLD = 10;
const LOW_CREDITS_PROMPT_KEY = "wembii_low_credits_prompt_no_again";

export const AiCreditsPopover = ({ companyPlan, className }: Props) => {
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const { companyId } = useMeCore();
  const { goToAIGeneration } = useRouterCore();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [monthlyAllowance, setMonthlyAllowance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [lowCreditsPromptOpen, setLowCreditsPromptOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const didPromptThisSessionRef = useRef(false);

  const loadCredits = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await socialApiClient().get("nanobanana/credits", {
        params: { companyId },
      });
      const payload = data?.payload;
      if (payload && typeof payload.credits === "number") {
        setCredits(payload.credits);
        setMonthlyAllowance(payload.monthlyAllowance ?? null);
      } else {
        setCredits(null);
      }
    } catch {
      setCredits(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  useEffect(() => {
    const onCreditsRefresh = () => {
      loadCredits();
    };
    window.addEventListener(WEMBII_AI_CREDITS_REFRESH_EVENT, onCreditsRefresh);
    return () => {
      window.removeEventListener(
        WEMBII_AI_CREDITS_REFRESH_EVENT,
        onCreditsRefresh
      );
    };
  }, [loadCredits]);

  useEffect(() => {
    didPromptThisSessionRef.current = false;
    setLowCreditsPromptOpen(false);
  }, [companyId]);

  useEffect(() => {
    if (credits === null || loading) return;
    if (credits > LOW_CREDITS_THRESHOLD) return;
    if (!companyId) return;
    if (didPromptThisSessionRef.current) return;
    if (buyModalOpen) return;

    didPromptThisSessionRef.current = true;
    const key = `${LOW_CREDITS_PROMPT_KEY}_${companyId}`;

    try {
      const noAgain = window.localStorage.getItem(key) === "true";
      if (!noAgain) setLowCreditsPromptOpen(true);
    } catch {
      setLowCreditsPromptOpen(true);
    }
  }, [buyModalOpen, companyId, credits, loading]);

  useEffect(() => {
    if (lowCreditsPromptOpen) setDontShowAgain(false);
  }, [lowCreditsPromptOpen]);

  const closeLowCreditsPrompt = useCallback(() => {
    if (companyId && dontShowAgain) {
      try {
        window.localStorage.setItem(
          `${LOW_CREDITS_PROMPT_KEY}_${companyId}`,
          "true"
        );
      } catch {
        // ignore localStorage errors
      }
    }
    setLowCreditsPromptOpen(false);
  }, [companyId, dontShowAgain]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) loadCredits();
      return next;
    });
  }, [loadCredits]);

  const displayCredits =
    loading && credits === null
      ? "…"
      : credits !== null
      ? String(credits)
      : "—";

  return (
    <>
      <Dropdown
        isOpen={open}
        toggle={toggle}
        className={cn("ai-credits-dropdown", className)}
      >
        <DropdownToggle
          tag="button"
          type="button"
          {...{ [WEMBII_AI_CREDITS_TOGGLE_ATTR]: "" }}
          caret={false}
          className={`ai-credits-popover__trigger ${
            isDarkMode ? "ai-credits-popover__trigger--dark" : ""
          }`}
          aria-label={intl.formatMessage({ id: "app.aiCredits.triggerAria" })}
        >
          <FontAwesomeIcon
            icon={faBolt}
            className="ai-credits-popover__bolt"
          />
          <span className="ai-credits-popover__count">{displayCredits}</span>
        </DropdownToggle>
        <DropdownMenu
          right
          className={`ai-credits-popover__menu border-0 shadow ${
            isDarkMode ? "ai-credits-popover__menu--dark" : ""
          }`}
        >
          <div className="ai-credits-popover__panel">
            {companyPlan && (
              <div className="ai-credits-popover__header">
                <span className="ai-credits-popover__plan-name">
                  {intl.formatMessage({ id: companyPlan.name })}
                </span>
                <Badge pill className="ai-credits-popover__status">
                  {intl.formatMessage({ id: "app.aiCredits.planActive" })}
                </Badge>
              </div>
            )}
            {!companyPlan && (
              <div className="ai-credits-popover__header ai-credits-popover__header--solo">
                <span className="ai-credits-popover__plan-name">
                  {intl.formatMessage({ id: "app.aiCredits.panelTitle" })}
                </span>
              </div>
            )}
            <div className="ai-credits-popover__credits-row">
              <span>
                {intl.formatMessage({ id: "app.aiCredits.labelCredits" })}
              </span>
              <span className="ai-credits-popover__credits-value">
                <FontAwesomeIcon
                  icon={faBolt}
                  className="ai-credits-popover__bolt"
                />
                {loading && credits === null
                  ? "…"
                  : credits !== null
                  ? credits
                  : "—"}
              </span>
            </div>
            {monthlyAllowance !== null && monthlyAllowance > 0 && (
              <div className="ai-credits-popover__monthly-block">
                <p className="ai-credits-popover__monthly-hint mb-0">
                  {intl.formatMessage(
                    { id: "app.aiCredits.monthlyInfo" },
                    { amount: monthlyAllowance }
                  )}
                </p>
                <p className="ai-credits-popover__monthly-hint ai-credits-popover__monthly-hint--renewal mb-0">
                  {intl.formatMessage({ id: "app.aiCredits.monthlyRenewalHint" })}
                </p>
              </div>
            )}
            <button
              type="button"
              className="ai-credits-popover__btn"
              onClick={() => {
                setOpen(false);
                setBuyModalOpen(true);
              }}
            >
              {intl.formatMessage({ id: "app.aiCredits.addMore" })}
            </button>
            <button
              type="button"
              className="ai-credits-popover__btn ai-credits-popover__btn--secondary"
              onClick={() => {
                setOpen(false);
                goToAIGeneration();
              }}
            >
              {intl.formatMessage({ id: "app.aiCredits.learnHow" })}
            </button>
          </div>
        </DropdownMenu>
      </Dropdown>
      <BuyCreditsModal
        isOpen={buyModalOpen}
        toggle={() => setBuyModalOpen(false)}
        onPurchaseComplete={() => {
          loadCredits();
          setBuyModalOpen(false);
        }}
      />
      <LowCreditsPromptOverlay
        isOpen={lowCreditsPromptOpen}
        dontShowAgain={dontShowAgain}
        credits={credits ?? 0}
        onDontShowAgainChange={setDontShowAgain}
        onLater={closeLowCreditsPrompt}
        onBuyNow={() => {
          closeLowCreditsPrompt();
          setBuyModalOpen(true);
        }}
      />
    </>
  );
};
